"use client";

import { useCallback } from "react";
import { DesktopVirtualizedTable } from "@/components/shared/DesktopVirtualizedTable";
import { OpenPositionTableRow } from "./OpenPositionTableRow";
import type { OpenPositionTableRowData } from "./types";
import {
  OPEN_POSITIONS_COL_SPAN,
  OPEN_POSITIONS_TABLE_CLASS,
} from "./openPositionsTableLayout";
import type { OpenedTrade } from "@/vybe-trader-client/opened-trades";
import type { BulkCloseMode } from "@/vybe-trader-client/connection/client/VybeTraderClient";

export interface OpenPositionsTableProps {
  rows: OpenPositionTableRowData[];
  emptyMessage: string;
  header: React.ReactNode;
  focusedTradeTicket: string | null;
  bulkCounts: Record<BulkCloseMode, number>;
  hasCloseByCandidate: (trade: OpenedTrade) => boolean;
  onCloseTrade: (trade: OpenedTrade) => void;
  onCloseBy: (trade: OpenedTrade) => void;
  onBulkClose: (mode: BulkCloseMode) => void;
  onModify: (trade: OpenedTrade) => void;
  onClearLevel: (trade: OpenedTrade, level: "SL" | "TP") => void;
  /** Investor (read-only) session: rows render without close/modify controls. */
  readOnly?: boolean;
}

export function OpenPositionsTable({
  rows,
  emptyMessage,
  header,
  focusedTradeTicket,
  bulkCounts,
  hasCloseByCandidate,
  onCloseTrade,
  onCloseBy,
  onBulkClose,
  onModify,
  onClearLevel,
  readOnly = false,
}: OpenPositionsTableProps) {
  const getRowKey = useCallback(
    (index: number) => rows[index]?.trade.ticket ?? index,
    [rows]
  );

  const renderRow = useCallback(
    (index: number) => {
      const row = rows[index];
      if (!row) return null;
      const isSelected =
        focusedTradeTicket != null &&
        focusedTradeTicket === row.trade.ticket.toString();
      const actionLabel = row.isPending
        ? "Cancel pending order"
        : "Close position";

      return (
        <OpenPositionTableRow
          key={row.trade.ticket}
          row={row}
          isSelected={isSelected}
          actionLabel={actionLabel}
          hasCloseByCandidate={hasCloseByCandidate(row.trade)}
          bulkCounts={bulkCounts}
          onCloseTrade={onCloseTrade}
          onCloseBy={onCloseBy}
          onBulkClose={onBulkClose}
          onModify={onModify}
          onClearLevel={onClearLevel}
          readOnly={readOnly}
        />
      );
    },
    [
      rows,
      focusedTradeTicket,
      bulkCounts,
      hasCloseByCandidate,
      onCloseTrade,
      onCloseBy,
      onBulkClose,
      onModify,
      onClearLevel,
      readOnly,
    ]
  );

  return (
    <DesktopVirtualizedTable
      rowCount={rows.length}
      colSpan={OPEN_POSITIONS_COL_SPAN}
      emptyMessage={emptyMessage}
      tableClassName={OPEN_POSITIONS_TABLE_CLASS}
      header={header}
      getRowKey={getRowKey}
      renderRow={renderRow}
    />
  );
}
